import {useNavigate, useParams} from 'react-router-dom';
import { useState, useEffect } from 'react';
import {get} from '../helpers/httpClient';
import { usePosterColors } from '../PosterContext';
import { useBreadcrumbs } from '../BreadcrumbsContext';
import TemplateRows from './component/TemplateRows';

const pageCache = {};

function App() {
    const params = useParams();
    const navigate = useNavigate();
    const { setPosterColors } = usePosterColors();
    const { setBreadcrumbs } = useBreadcrumbs();

    const slug = params['*'] || '';
    const [page, setPage] = useState(pageCache[slug]?.page || null);

    useEffect(() => {
        setBreadcrumbs(pageCache[slug]?.breadcrumbs || []);
    }, [slug, setBreadcrumbs]);

    const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 768);

    useEffect(() => {
        const handleResize = () => setIsLargeScreen(window.innerWidth > 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    useEffect(() => {
        const getPage = async () => {
            try {
                const response = await get('pages?slug=' + slug);
                if (response) {
                    pageCache[slug] = { page: response.page, breadcrumbs: response.breadcrumbs || [] };
                    setPage(response.page);
                    setBreadcrumbs(response.breadcrumbs || []);
                }
            } catch (error) {
                navigate('/404')
            }
        };

        getPage();
    }, [slug]);

    useEffect(() => {
        if (page?.title) {
            document.title = page.title;
        }
    }, [page?.title]);

    // A poster can be embedded as a Layout component on any page (e.g. the home page's hero)
    // — its config lives in template.props, and still needs the same body-level styling.
    const posterExtra = Object.values(page?.template?.props || {}).find(v => v && typeof v === 'object' && v.type === 'poster')?.config;

    useEffect(() => {
        if (posterExtra) {
            document.body.classList.add('poster-page');
            const { overlayColor, textShadeColor } = posterExtra || {};
            setPosterColors({ overlayColor, textShadeColor });
            return () => {
                document.body.classList.remove('poster-page');
                setPosterColors(null);
            };
        }
    }, [posterExtra, setPosterColors]);

    useEffect(() => {
        if (!posterExtra) return;
        const { background, backgroundVertical, backgroundType } = posterExtra || {};
        if (backgroundType !== 'image') return;

        const links = [];
        const addLink = (href, media) => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = href;
            if (media) link.media = media;
            document.head.appendChild(link);
            links.push(link);
        };

        if (background && backgroundVertical) {
            addLink(background, '(orientation: landscape)');
            addLink(backgroundVertical, '(orientation: portrait)');
        } else if (background || backgroundVertical) {
            addLink(background || backgroundVertical);
        }

        return () => links.forEach(l => l.remove());
    }, [posterExtra?.background, posterExtra?.backgroundVertical, posterExtra?.backgroundType]);

    if (!page) return null;

    const contentNode = (
        <div className="d-flex justify-content-center flex-column">
            {page.template && (
                <TemplateRows
                    rows={page.template.rows}
                    mobileColumn={page.template.mobile_column}
                    props={page.template.props}
                    isMobile={!isLargeScreen}
                    titleFallback={page.caption || page.title}
                    pageTitle={page.title}
                    children={page.children}
                />
            )}
        </div>
    );

    return (
        <div key={page.slug} className="page-container page-appear">
            <div className="page-layout">
                <div className="page-stack">
                    <div className="page-center">{contentNode}</div>
                </div>
            </div>
        </div>
    );
}

export default App;
